import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedNumber } from 'react-intl';

import { App, Button, Modal, Spin, Tooltip, Typography } from 'antd';

import ColoredFormattedNumberForeign from 'components/ColoredFormattedNumberForeign';
import ModalActions from 'components/ModalActions';
import { useBankTransfer } from 'components/Transaction/useBankTransfer';
import { constants, FINANCIAL_BLOCK_TOOLTIP_MESSAGE } from 'helpers/constants';
import WithBetaModules from 'v2/components/Shared/WithBetaModules';
import { useProfileListAccounts } from 'v2/hooks/useProfileListAccounts';
import { useUserStore } from 'v2/store/User';

import AccountList from './components/AccountList';

const MIN_VALUE_TO_TRANSFER_DOLAR = 1000;
const MIN_VALUE_TO_TRANSFER_REAL = 5500;
const MIN_TRANSFER_VALUE_USD = 100;

interface IProps {
  value: any;
  selfClose: any;
  successCallback: any;
  open: boolean;
  onCloseModalTransfer: any;
}

const TransferForeign = ({ value, selfClose, successCallback, open, onCloseModalTransfer }: IProps) => {
  const { message } = App.useApp();
  const [modalTransfers, setModalTransfers] = useState(false);
  const [changeAccountList, setChangeAccountList] = useState(false);
  const [accountActive, setAccountActive] = useState<any>({});
  const [disableTransfer, setDisableTransfer] = useState(false);
  const { data: accounts, isLoading: isLoadingAccounts } = useProfileListAccounts(true);

  const { crossborder } = useUserStore(({ user }) => ({ crossborder: user.crossborder }));

  const accountActiveRef = useRef(accountActive);
  accountActiveRef.current = accountActive;

  const minAbleToTransfer = crossborder ? MIN_VALUE_TO_TRANSFER_DOLAR : MIN_VALUE_TO_TRANSFER_REAL;
  const currency = crossborder ? 'USD' : 'BRL';

  const { bankTransfer, isPending: isPendingBankTransfer } = useBankTransfer();

  useEffect(() => {
    if (value < minAbleToTransfer) {
      setDisableTransfer(true);
    }
  }, [value, minAbleToTransfer]);

  useEffect(() => {
    if (!isLoadingAccounts && accounts?.length > 0) {
      setAccountActive(accounts[0]);
    }
  }, [accounts, isLoadingAccounts]);

  const handleClickTransfers = useCallback(
    (transferValue: number) => {
      const account = accounts.length ? accountActiveRef.current.id : null;
      setModalTransfers(false);

      let success = true;

      if (!account) {
        success = false;
        message.error('Por favor cadastre uma conta bancária.');
      }

      if (transferValue < 0) {
        success = false;
        message.error('Valor inválido para transferência.');
      }

      if (transferValue < MIN_TRANSFER_VALUE_USD) {
        success = false;
        message.error('O valor da transferência não pode ser menor que US$ 100,00.');
      }

      if (success) {
        (async () => {
          try {
            if (transferValue > 0) {
              setDisableTransfer(true);
              await bankTransfer({
                value: transferValue,
                transfer_method_id: account,
                commissions: []
              });
            }

            if (successCallback) {
              successCallback(true);
            }

            message.success('Sucesso, transferência realizada com êxito');

            if (successCallback) {
              successCallback(false);
            }

            selfClose();
          } catch {
            message.error('Ops, erro na transferência');
          } finally {
            setDisableTransfer(false);
          }
        })();
      }
    },
    [accounts, successCallback, selfClose, bankTransfer, message]
  );

  const handleOnClick = useCallback(() => {
    setModalTransfers(true);
  }, []);

  const onCancelModalTransfers = useCallback(() => {
    setModalTransfers(false);
  }, []);

  const handleChangeAccount = useCallback((account: any) => {
    setAccountActive(account);
    setChangeAccountList(prev => !prev);
  }, []);

  const toggleChangeAccount = useCallback(() => {
    setChangeAccountList(prev => !prev);
  }, []);

  const renderTooltipValue = useMemo(() => {
    if (value < minAbleToTransfer) {
      return (
        <p>
          Valor mínimo para transferir: <FormattedNumber value={minAbleToTransfer} format={currency}></FormattedNumber>
        </p>
      );
    }

    return '';
  }, [value, minAbleToTransfer, currency]);

  return (
    <>
      <Modal
        open={open}
        onCancel={onCloseModalTransfer}
        centered
        width={650}
        title={changeAccountList ? 'Alteração de conta bancária' : 'Transferência bancária'}
        footer={
          !changeAccountList ? (
            <div className='flex justify-end'>
              <Button type='text' onClick={onCloseModalTransfer}>
                Cancelar
              </Button>
            </div>
          ) : null
        }
      >
        <div className='flex max-h-[80vh] flex-col gap-4 overflow-auto'>
          {!changeAccountList && (
            <>
              <div className='rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
                <div className='flex flex-wrap items-baseline justify-between gap-2'>
                  <Typography.Text>Valor a transferir:</Typography.Text>
                  <ColoredFormattedNumberForeign value={value || 10} format={currency} />
                </div>
              </div>

              {!isLoadingAccounts && accounts?.length > 0 && (
                <div className='flex flex-col gap-1'>
                  <Typography.Text>
                    Transferir para Banco: <Typography.Text strong>{accountActive.bank_id}</Typography.Text>, Ag:{' '}
                    <Typography.Text strong>
                      {accountActive.agency}-{accountActive.agency_digit}
                    </Typography.Text>
                    , Conta:{' '}
                    <Typography.Text strong>
                      {accountActive.account}-{accountActive.account_digit}
                    </Typography.Text>
                    .
                  </Typography.Text>
                  <Typography.Link onClick={toggleChangeAccount}>Clique aqui para alterar a conta</Typography.Link>
                </div>
              )}
              {isLoadingAccounts && (
                <div className='flex w-full justify-center'>
                  <Spin size='small' />
                </div>
              )}
              {!isLoadingAccounts && (!accounts || accounts.length === 0) && (
                <Typography.Text>Nenhuma conta bancária está ativa.</Typography.Text>
              )}

              <WithBetaModules>
                {(betaModules: string[]) => {
                  const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');
                  const renderTooltipBlockTransfer = userHasBlockTransferBetaModule
                    ? FINANCIAL_BLOCK_TOOLTIP_MESSAGE
                    : false;
                  return (
                    <Tooltip title={renderTooltipBlockTransfer || renderTooltipValue} placement='top'>
                      <div className='w-full'>
                        <Button
                          type='primary'
                          block
                          onClick={handleOnClick}
                          disabled={disableTransfer || userHasBlockTransferBetaModule}
                          loading={isPendingBankTransfer}
                        >
                          Transferir
                        </Button>
                      </div>
                    </Tooltip>
                  );
                }}
              </WithBetaModules>
            </>
          )}

          {changeAccountList && (
            <AccountList
              handleChangeAccount={handleChangeAccount}
              accountDefault={accountActive}
              listAccounts={accounts}
              toggleChangeAccount={toggleChangeAccount}
            />
          )}
        </div>
      </Modal>

      <ModalActions
        open={modalTransfers}
        onClose={onCancelModalTransfers}
        onOk={() => handleClickTransfers(value)}
        title='Atenção'
        buttonOk='Continuar'
      >
        <p>
          Você solicitou uma transferência.
          <br />
          Neste valor será descontado os encargos de transferência no valor de{' '}
          <ColoredFormattedNumberForeign value={constants.TRANSFER_RATE_VALUE} format={currency} />. Então o valor total
          transferido para sua conta será de{' '}
          <Typography.Text strong>
            <ColoredFormattedNumberForeign value={value - constants.TRANSFER_RATE_VALUE} format={currency} />
          </Typography.Text>
          .
          <br />
          Deseja continuar com a transação?
        </p>
      </ModalActions>
    </>
  );
};

export default TransferForeign;
